import React from 'react';
import { Microscope } from 'lucide-react';
import DashboardCard from '@/components/ui/DashboardCard';
import GlassPill from '@/components/ui/GlassPill';
import { getCountryDisplayName } from '@/lib/countries';

// Reuse existing research components
import ResearchCardGrid from '@/components/research-generator/ResearchCardGrid';
import ResearchLoadingStates from '@/components/research-generator/ResearchLoadingStates';
import ResearchEmptyState from '@/components/research-generator/ResearchEmptyState';
import ResearchLoadMoreButton from '@/components/research-generator/ResearchLoadMoreButton';
import ResearchCardSkeleton from '@/components/research-generator/ResearchCardSkeleton';

interface PrivateResearchMainLayoutProps {
  allResearch: any[];
  transformedResearch: any[];
  isLoading: boolean;
  error: any;
  searchTerm: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onResearchClick: (research: any) => void;
  onToggleFavorite: (researchId: string) => Promise<void>;
  onLoadMore: () => void;
  title?: string; // Optional title prop for dynamic project names
  activeFilters?: {
    researchType?: string;
    timeframe?: string;
    country?: string;
    sourceType?: string;
    projectName?: string;
  };
}

export const PrivateResearchMainLayout: React.FC<PrivateResearchMainLayoutProps> = ({
  allResearch,
  transformedResearch,
  isLoading,
  error,
  searchTerm,
  hasNextPage,
  isFetchingNextPage,
  onResearchClick,
  onToggleFavorite,
  onLoadMore,
  title = "Your Research", // Default to "Your Research" if no title provided
  activeFilters = {},
}) => {
  // Helper function to format filter values for display
  const formatFilterValue = (key: string, value: string): string => {
    // Format timeframe values
    if (key === 'timeframe') {
      const timeframeMap: Record<string, string> = {
        'last_30_days': 'Last 30 Days',
        'last_3_months': 'Last 3 Months',
        'last_6_months': 'Last 6 Months',
        'last_12_months': 'Last 12 Months',
        'this_year': 'This Year',
        'last_year': 'Last Year',
        'last_2_years': 'Last 2 Years',
        'last_3_years': 'Last 3 Years',
        'last_5_years': 'Last 5 Years',
        'last_10_years': 'Last 10 Years',
        'all': 'All Time',
        'all_time': 'All Time'
      };
      return timeframeMap[value] || value;
    }
    
    // Format country codes using the same function as the sidebar
    if (key === 'country') {
      if (value === 'global') return 'Global';
      return getCountryDisplayName(value);
    }
    
    // Format source types
    if (key === 'sourceType') {
      const sourceMap: Record<string, string> = {
        'academic': 'Academic Sources',
        'industry': 'Industry Reports',
        'news': 'News Articles',
        'mixed': 'Mixed Sources',
        'educational': 'Educational Resources'
      };
      return sourceMap[value] || value;
    }
    
    // For other values, convert snake_case to Title Case
    return value.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Build array of active filters for display
  const getActiveFilters = () => {
    const filters = [];
    
    if (searchTerm) {
      filters.push(`"${searchTerm}"`);
    }
    
    // Don't show project name since it's already displayed in the title
    // if (activeFilters.projectName) {
    //   filters.push(activeFilters.projectName);
    // }
    
    if (activeFilters.researchType && activeFilters.researchType !== 'none') {
      filters.push(activeFilters.researchType);
    }
    
    
    if (activeFilters.timeframe && activeFilters.timeframe !== 'none') {
      filters.push(formatFilterValue('timeframe', activeFilters.timeframe));
    }
    
    if (activeFilters.country && activeFilters.country !== 'none') {
      filters.push(formatFilterValue('country', activeFilters.country));
    }
    
    if (activeFilters.sourceType && activeFilters.sourceType !== 'none') {
      filters.push(formatFilterValue('sourceType', activeFilters.sourceType));
    }
    
    return filters;
  };
  return (
    <DashboardCard variant="purple" className="p-3 md:p-6">
        <div className="space-y-4">
          {/* Header Section (matching Project Research Library style) */}
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="flex items-center gap-2">
              <Microscope className="text-purple-500 dark:text-purple-400" size={18} />
              <h3 className="font-medium text-base">{title}</h3>
              <GlassPill 
                type={allResearch.length.toString()} 
                variant="purple" 
                className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
              />
            </div>
          </div>
          
          {/* Search feedback */}
          {(() => {
            const activeFiltersList = getActiveFilters();
            return activeFiltersList.length > 0 && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex flex-wrap items-center gap-1">
                <span>Showing results for:</span>
                {activeFiltersList.map((filter, index) => (
                  <React.Fragment key={index}>
                    <span className="font-medium">{filter}</span>
                    {index < activeFiltersList.length - 1 && <span>•</span>}
                  </React.Fragment>
                ))}
              </p>
            );
          })()}
          
          {/* Content */}
          <div className="space-y-2">
            {/* NEW: Skeleton loading during search/filter operations (like Image Generator) */}
            {isLoading && transformedResearch.length === 0 ? (
              <div className="grid grid-cols-1 2xl:grid-cols-2 gap-2 md:gap-6">
                {Array.from({ length: 6 }).map((_, index) => (
                  <ResearchCardSkeleton key={`skeleton-${index}`} index={index} />
                ))}
              </div>
            ) : (
              <>
                {/* Loading States */}
                <ResearchLoadingStates
                  isLoading={isLoading}
                  error={error ? new Error(error.toString()) : undefined}
                  hasData={transformedResearch.length > 0}
                />

                {/* Empty State */}
                <ResearchEmptyState
                  isLoading={isLoading}
                  hasError={!!error}
                  hasData={transformedResearch.length > 0}
                  searchTerm={searchTerm} // NEW: Pass search term for context-aware messaging
                />

                {/* Research Grid */}
                <ResearchCardGrid
                  research={transformedResearch}
                  isLoading={isLoading}
                  onResearchClick={onResearchClick}
                  onToggleFavorite={onToggleFavorite}
                  showFavoriteButton={false}
                  contentType="private_research"
                  hideStorageButton={true}
                />

                {/* Load More Button */}
                <ResearchLoadMoreButton
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  onLoadMore={onLoadMore}
                />
              </>
            )}
          </div>
        </div>
      </DashboardCard>
    );
}; 